import { isAccountBlockedError } from './auth-errors';
import { hasClientAuthCookies } from './auth-cookies';

type AuthTokens = {
  accessToken: string;
  refreshToken: string;
};

export type TelegramMiniAppAuthError =
  | 'telegram_environment_unavailable'
  | 'telegram_auth_unavailable'
  | 'telegram_auth_failed'
  | 'account_blocked';

export type TelegramMiniAppBootstrapOutcome =
  | { type: 'skip' }
  | { type: 'login'; initData: string }
  | { type: 'block'; error: TelegramMiniAppAuthError };

type TelegramMiniAppBootstrapInput = {
  cookieString: string;
  initData?: string | null;
  isTelegramEnvironment: boolean;
  forceLogin?: boolean;
};

type BootstrapTelegramMiniAppAuthInput = TelegramMiniAppBootstrapInput & {
  authorize: (initData: string) => Promise<AuthTokens>;
  persistTokens: (tokens: AuthTokens) => Promise<void> | void;
  onAuthSuccess?: () => Promise<void> | void;
};

export function getTelegramMiniAppBootstrapOutcome({
  cookieString,
  forceLogin = false,
  initData,
  isTelegramEnvironment,
}: TelegramMiniAppBootstrapInput): TelegramMiniAppBootstrapOutcome {
  if (!forceLogin && hasClientAuthCookies(cookieString)) {
    return { type: 'skip' };
  }

  if (!isTelegramEnvironment) {
    return { type: 'block', error: 'telegram_environment_unavailable' };
  }

  const normalizedInitData = initData?.trim();
  if (!normalizedInitData) {
    return { type: 'block', error: 'telegram_auth_unavailable' };
  }

  return { type: 'login', initData: normalizedInitData };
}

export async function bootstrapTelegramMiniAppAuth({
  authorize,
  persistTokens,
  onAuthSuccess,
  ...input
}: BootstrapTelegramMiniAppAuthInput) {
  const outcome = getTelegramMiniAppBootstrapOutcome(input);
  if (outcome.type === 'skip') {
    return {
      status: 'ready' as const,
      performedLogin: false,
    };
  }

  if (outcome.type === 'block') {
    return {
      status: 'blocked' as const,
      error: outcome.error,
    };
  }

  try {
    const tokens = await authorize(outcome.initData);

    if (!tokens.accessToken || !tokens.refreshToken) {
      throw new Error('Missing auth tokens');
    }

    await persistTokens(tokens);
    await onAuthSuccess?.();

    return {
      status: 'ready' as const,
      performedLogin: true,
    };
  } catch (e: any) {
    return {
      status: 'blocked' as const,
      error: isAccountBlockedError(e) ? ('account_blocked' as const) : ('telegram_auth_failed' as const),
    };
  }
}

export function getTelegramMiniAppRedirectTarget(rawRedirect: string | null | undefined) {
  if (!rawRedirect) {
    return null;
  }

  let decodedRedirect = rawRedirect;

  try {
    decodedRedirect = decodeURIComponent(rawRedirect);
  } catch {
    decodedRedirect = rawRedirect;
  }

  if (!decodedRedirect.startsWith('/') || decodedRedirect.includes('//')) {
    return '/';
  }

  return decodedRedirect;
}
